<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { signupInsights } from '@/api';
import { extractErrorCode, extractErrorDetail, gateErrorMessage } from '@/utils/errorHandler';
import { isRecaptchaAvailable, recaptchaHeaders } from '@/utils/recaptcha';
import { event } from 'vue-gtag';
import { logger } from '@/utils/logger';
import GaiaAuthCard from '@/components/design/domain/auth/GaiaAuthCard.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaSegmented from '@/components/design/base/GaiaSegmented.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';

const authStore = useAuthStore();
const router = useRouter();
const error = ref<string | null>(null);
const submitting = ref<boolean>(false);

const fullName = ref<string>('');
const companyName = ref<string>('');
const contractTypes = ref<string[]>([]);
const products = ref<string[]>([]);
const companySize = ref<string>('');
const hostingZone = ref<string>('');

// Options for select fields
const contractTypeOptions = [
  { label: 'Commercial', value: 'commercial' },
  { label: 'IP & IT', value: 'ip_it' },
  { label: 'HR', value: 'hr' },
  { label: 'Legal & Dispute Resolution', value: 'legal_dispute_resolution' },
  { label: 'Real Estate', value: 'real_estate' },
  { label: 'Logistics', value: 'logistics' },
  { label: 'Corporate & Governance', value: 'corporate_governance' },
];

const productOptions = [
  { label: 'AI Risk Reports', value: 'ai_risk_reports' },
  { label: 'AI Contract Reviews', value: 'ai_contract_reviews' },
  { label: 'Contract Management', value: 'contract_management' },
  { label: 'Equity Management', value: 'equity_management' },
];

const hostingZoneOptions = [
  { label: 'US', value: 'us' },
  { label: 'EU', value: 'eu' },
];

const companySizeOptions = [
  { label: '1-10 employees', value: '1-10' },
  { label: '11-50 employees', value: '11-50' },
  { label: '51-200 employees', value: '51-200' },
  { label: '201-500 employees', value: '201-500' },
  { label: '501+ employees', value: '501+' },
];

onMounted((): void => {
  if (!authStore.user) {
    // The router guard normally intercepts sessionless visits — defensive.
    void router.replace('/sign-up');
    return;
  }
  fullName.value = authStore.user.displayName ?? '';
});

/**
 * The chip groups are multi-select, so a tap flips membership. Takes the array
 * rather than the ref: templates hand over the unwrapped value, and mutating it
 * in place is the same reactive array either way.
 */
function toggleIn(list: string[], value: string): void {
  const index = list.indexOf(value);
  if (index === -1) list.push(value);
  else list.splice(index, 1);
}

async function handleProfileCompletion(): Promise<void> {
  // Validate form
  if (!fullName.value || !companyName.value) {
    error.value = 'Please fill in all required fields';
    return;
  }

  if (contractTypes.value.length === 0) {
    error.value = 'Please select at least one contract type';
    return;
  }

  if (products.value.length === 0) {
    error.value = 'Please select at least one product';
    return;
  }

  if (!hostingZone.value) {
    error.value = 'Please select your hosting zone';
    return;
  }

  if (!companySize.value) {
    error.value = 'Please select your company size';
    return;
  }

  if (!isRecaptchaAvailable()) {
    // The reCAPTCHA plugin is not registered in the Office add-in host.
    error.value = `Please finish signing up in your browser at ${window.location.origin}/sign-up/complete`;
    return;
  }

  submitting.value = true;
  error.value = null;
  try {
    // Identity (uid + email) comes from the Firebase ID token the API client
    // attaches — the body carries profile data only.
    const { error: apiError, response } = await signupInsights({
      body: {
        full_name: fullName.value,
        company_name: companyName.value,
        contracts_used: contractTypes.value,
        products_interested_in: products.value,
        company_size: companySize.value,
        hosting_zone: hostingZone.value,
      },
      headers: await recaptchaHeaders('signup'),
      throwOnError: false,
    });

    if (apiError) {
      const gateMessage = gateErrorMessage(response, apiError);
      if (gateMessage) {
        error.value = gateMessage;
        return;
      }
      const code = extractErrorCode(apiError);
      if (code === 'email_unverified') {
        void router.replace('/verify-email');
        return;
      }
      if (code === 'email_conflict') {
        error.value = 'An account for this email already exists — try logging in instead.';
        return;
      }
      logger.error('Profile completion error:', apiError);
      error.value = extractErrorDetail(
        apiError,
        'Error completing your profile. Please try again.'
      );
      return;
    }

    await authStore.refreshBackendValidation();

    if (import.meta.env.VITE_GA_MEASUREMENT_ID) {
      // Use a more explicit event name for ads tracking
      event('conversion', {
        send_to: 'AW-17083153316/meLWCKPHn8gaEKT38NE_',
        value: 1,
        currency: 'EUR',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
        transaction_id: authStore.user?.uid || Date.now().toString(),
      });

      // Additionally log a regular GA event for redundancy
      event('sign_up_completed', {
        method: authStore.user?.providerData[0]?.providerId === 'google.com' ? 'google' : 'email',
        user_id: authStore.user?.uid,
      });
    }

    await new Promise(resolve => setTimeout(resolve, 500));
    // Redirect to home page after successful signup
    void router.push({
      path: '/',
      query: {
        'first-sign-up': '',
        'contract-types': contractTypeOptions
          .filter(option => contractTypes.value.includes(option.value))
          .map(option => option.label)
          .join(','),
      },
    });
  } catch (error_) {
    logger.error('Profile completion error:', error_);
    error.value = 'Error completing your profile. Please try again.';
  } finally {
    submitting.value = false;
  }
}

async function handleSignOut(): Promise<void> {
  await authStore.logout();
  void router.replace('/login');
}
</script>

<template>
  <gaia-auth-card
    width="560px"
    title="Complete your profile"
    subtitle="Tell us a bit about your needs."
  >
    <n-flex vertical :size="20">
      <!-- Name and company share a row: two short answers that belong to the
           same question about who you are. -->
      <n-grid :cols="2" :x-gap="14">
        <n-gi>
          <gaia-form-field label="Full name" variant="uppercase" required>
            <gaia-input
              v-model:value="fullName"
              size="large"
              placeholder="Sarah Lindqvist"
              :input-props="{ autocomplete: 'name' }"
              style="width: 100%"
            />
          </gaia-form-field>
        </n-gi>
        <n-gi>
          <gaia-form-field label="Company name" variant="uppercase" required>
            <gaia-input
              v-model:value="companyName"
              size="large"
              placeholder="Lindqvist & Partners"
              :input-props="{ autocomplete: 'organization' }"
              style="width: 100%"
            />
          </gaia-form-field>
        </n-gi>
      </n-grid>

      <!-- Signing up always provisions a fresh organisation. Colleagues of an
           existing customer kept landing in a same-named duplicate, so say it
           out loud and point them at the invite path instead. -->
      <gaia-banner plain type="warning" title="You're creating a new organisation">
        If your organisation already uses Gaia Insights, don't create a second one: ask your
        administrator to invite you instead. If you continue, you can still accept an invitation
        later. You'll then belong to both organisations and can switch between them.
      </gaia-banner>

      <!-- Chips rather than a multi-select: seven options the user should be
           able to read at once, on the step whose whole job is answering them. -->
      <gaia-form-field label="Which contracts do you use?" variant="uppercase">
        <n-flex :size="7">
          <gaia-tag
            v-for="option in contractTypeOptions"
            :key="option.value"
            pill
            checkable
            size="medium"
            :checked="contractTypes.includes(option.value)"
            @update:checked="toggleIn(contractTypes, option.value)"
          >
            {{ option.label }}
          </gaia-tag>
        </n-flex>
      </gaia-form-field>

      <gaia-form-field label="Which products do you need?" variant="uppercase">
        <n-flex :size="7">
          <gaia-tag
            v-for="option in productOptions"
            :key="option.value"
            pill
            checkable
            size="medium"
            :checked="products.includes(option.value)"
            @update:checked="toggleIn(products, option.value)"
          >
            {{ option.label }}
          </gaia-tag>
        </n-flex>
      </gaia-form-field>

      <n-grid :cols="3" :x-gap="14">
        <n-gi>
          <gaia-form-field label="Hosting zone" variant="uppercase">
            <gaia-segmented
              v-model:value="hostingZone"
              block
              size="medium"
              :options="hostingZoneOptions.map(o => ({ value: o.value, label: o.label }))"
            />
          </gaia-form-field>
        </n-gi>
        <n-gi :span="2">
          <gaia-form-field label="Company size" variant="uppercase">
            <gaia-segmented
              v-model:value="companySize"
              block
              size="medium"
              :options="companySizeOptions.map(o => ({ value: o.value, label: o.value }))"
            />
          </gaia-form-field>
        </n-gi>
      </n-grid>

      <gaia-banner v-if="error" plain type="error">{{ error }}</gaia-banner>
    </n-flex>

    <gaia-button
      type="primary"
      size="large"
      block
      :loading="submitting"
      :style="{ marginTop: '26px' }"
      @click="handleProfileCompletion"
    >
      Complete sign up
    </gaia-button>

    <template #footer>
      By signing up you agree to our
      <gaia-link href="https://www.gaia.law/tos" target="_blank">terms and conditions</gaia-link>
      and
      <gaia-link href="https://www.gaia.law/privacy" target="_blank">privacy policy</gaia-link>.
    </template>
  </gaia-auth-card>

  <n-flex justify="center" :style="{ marginTop: '18px' }">
    <gaia-link tone="muted" @click="handleSignOut">Sign out</gaia-link>
  </n-flex>
</template>
